import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  VStack, HStack, Text, Heading, Box, Tag, Divider,
  SimpleGrid, Spinner, Center, Collapse, useDisclosure, Button
} from '@chakra-ui/react';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { useAuthCliente } from '../context/AuthClienteContext';
import { getPhaseName, getRoundLabelsForPoints } from '../utils/tournamentLogic'; // Asegúrate de que esta utilidad esté disponible
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

export default function TournamentBracketModal({ isOpen, onClose, tournament }) {
  const { currentUser } = useAuthCliente();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen: isParticipantsOpen, onToggle: onParticipantsToggle } = useDisclosure();

  useEffect(() => {
    const fetchParticipants = async () => {
      // Unificamos la lógica para leer 'participants' o 'players'
      const playerIds = tournament?.participants || tournament?.players || [];
      if (!isOpen || playerIds.length === 0) {
        setParticipants([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const playersData = [];
        const chunks = [];
        for (let i = 0; i < playerIds.length; i += 10) {
          chunks.push(playerIds.slice(i, i + 10));
        }
        const ref = collection(db, 'companies', currentUser.companyId, 'end_customers');
        for (const chunk of chunks) {
          if (chunk.length > 0) {
            const q = query(ref, where(documentId(), 'in', chunk));
            const snap = await getDocs(q);
            snap.forEach(doc => {
              playersData.push(doc.data().name);
            });
          }
        }
        setParticipants(playersData);
      } catch (error) {
        console.error("Error fetching participants:", error);
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) {
        fetchParticipants();
    }
  }, [tournament, currentUser.companyId, isOpen]);

  const sortedRounds = useMemo(() => {
    if (!tournament?.rounds) return [];
    const roundsArray = Object.values(tournament.rounds);
    const playerIds = tournament?.participants || tournament?.players || [];
    const totalPlayers = playerIds.length || 0;
    if (totalPlayers === 0) return roundsArray;
    const totalRounds = Math.ceil(Math.log2(2 ** Math.ceil(Math.log2(totalPlayers))));
    if (tournament.hasConsolation && roundsArray.length > totalRounds) {
      const final = roundsArray[roundsArray.length - 2];
      const consolation = roundsArray[roundsArray.length - 1];
      const otherRounds = roundsArray.slice(0, roundsArray.length - 2);
      return [...otherRounds, consolation, final];
    }
    return roundsArray;
  }, [tournament]);

  // ✅ Lógica para generar dinámicamente los puntos a mostrar
  const pointLabels = useMemo(() => {
    if (!tournament?.pointConfig || !tournament?.rounds) return [];
    
    const numRounds = Object.keys(tournament.rounds).length;
    const labels = getRoundLabelsForPoints(tournament.participants?.length || 0, tournament.hasConsolation);

    // Filtramos para mostrar solo las rondas que existen en el torneo
    return Object.entries(labels).filter(([key]) => {
        // La clave de los puntos (ej. 'winner', 'finalist', 'round_0')
        // debe tener un valor correspondiente en pointConfig
        return tournament.pointConfig[key] !== undefined;
    });

  }, [tournament]);

  if (!tournament) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{tournament.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? <Center h="full"><Spinner /></Center> :
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} h="full">
            <VStack align="stretch" spacing={4} p={4} bg="gray.50" borderRadius="lg">
              <Heading size="lg">Detalles</Heading>
              <HStack justify="space-between"><Text fontWeight="bold">Fecha:</Text><Text>{tournament.dates}</Text></HStack>
              <HStack justify="space-between"><Text fontWeight="bold">Categoría:</Text><Text>{tournament.category || 'N/A'}</Text></HStack>
              <HStack justify="space-between"><Text fontWeight="bold">Formato:</Text><Text>{tournament.format || 'N/A'}</Text></HStack>
              <HStack justify="space-between"><Text fontWeight="bold">Género:</Text><Text>{tournament.gender || 'N/A'}</Text></HStack>
              <HStack justify="space-between"><Text fontWeight="bold">Premio:</Text><Text>{tournament.prize || 'N/A'}</Text></HStack>
              <Divider />
              {/* ✅ SECCIÓN DE PUNTOS DINÁMICA */}
              {pointLabels.length > 0 && (
                <>
                  <Heading size="md">Puntos por Ronda</Heading>
                  <VStack align="stretch" spacing={1} fontSize="sm">
                    {pointLabels.map(([key, label]) => (
                      <HStack justify="space-between" key={key}>
                        <Text color="gray.600">{label}:</Text>
                        <Text fontWeight="bold">{tournament.pointConfig[key]} pts</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <Divider />
                </>
              )}
              <HStack justify="space-between" align="center">
                <Heading size="md">Participantes ({participants.length})</Heading>
                <Button size="sm" onClick={onParticipantsToggle}>
                  {isParticipantsOpen ? 'Ocultar' : 'Mostrar'}
                </Button>
              </HStack>
              <Collapse in={isParticipantsOpen} animateOpacity>
                <Box overflowY="auto" maxH="calc(100vh - 550px)" p={2} borderWidth="1px" bg="white" borderRadius="md">
                  {participants.length > 0 ? (
                    participants.map((name, index) => <Text key={`${name}-${index}`}>{name}</Text>)
                  ) : (
                    <Text color="gray.500">No hay participantes para mostrar.</Text>
                  )}
                </Box>
              </Collapse>
            </VStack>

            <VStack align="stretch" spacing={4} p={4} gridColumn={{ lg: "span 2" }}>
              <HStack>
                <Heading size="lg">Cuadro de Resultados</Heading>
                {/* ✅ ETIQUETA DE ESTADO DEL TORNEO */}
                {tournament.status === 'completed' && <Tag colorScheme="red">Torneo Finalizado</Tag>}
                {tournament.status === 'in-progress' && <Tag colorScheme="green">TORNEO EN CURSO</Tag>}
              </HStack>
              <Box overflowY="auto" h="calc(100vh - 200px)">
                {sortedRounds.map((round, roundIndex) => {
                  const originalPhaseIdx = Object.keys(tournament.rounds).find(key => tournament.rounds[key] === round) || roundIndex.toString();
                  const playersLength = tournament.participants?.length || 0;
                  const phaseName = getPhaseName(originalPhaseIdx, playersLength);
                  
                  return (
                  <Box key={originalPhaseIdx} mb={6}>
                    <Heading size="md" mb={3}>{phaseName}</Heading>
                    {round.map((match, matchIdx) => {
                      const winnerId = tournament.results?.[originalPhaseIdx]?.[matchIdx];
                      const matchScores = tournament.scores?.[originalPhaseIdx]?.[matchIdx] || [];
                      const matchDate = tournament.matchDates?.[originalPhaseIdx]?.[matchIdx]?.toDate();

                      return (
                        <VStack key={matchIdx} p={3} borderWidth="1px" borderRadius="md" bg="white" align="stretch" mb={2} boxShadow="sm">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={1} flex="1">
                              <Text fontWeight={winnerId === match.player1?.id ? 'bold' : 'normal'} color={winnerId === match.player1?.id ? 'green.600' : 'inherit'}>{match.player1?.name || '(Por definir)'}</Text>
                              <Text fontWeight={winnerId === match.player2?.id ? 'bold' : 'normal'} color={winnerId === match.player2?.id ? 'green.600' : 'inherit'}>{match.player2?.name || '(Por definir)'}</Text>
                            </VStack>
                            <Box w={{ base: "80px", md: "120px" }} textAlign="right">
                              {matchScores.length > 0 && winnerId ? (
                                <HStack spacing={3} justify="flex-end">
                                  {matchScores.map((set, i) => (
                                    <VStack key={i} spacing={1} bg="gray.100" p={1} borderRadius="sm">
                                      <Text fontSize="sm" lineHeight={1} fontWeight={parseInt(set.player1_score) > parseInt(set.player2_score) ? 'bold' : 'normal'}>{set.player1_score}</Text>
                                      <Divider/>
                                      <Text fontSize="sm" lineHeight={1} fontWeight={parseInt(set.player2_score) > parseInt(set.player1_score) ? 'bold' : 'normal'}>{set.player2_score}</Text>
                                    </VStack>
                                  ))}
                                </HStack>
                              ) : (match.player1 && match.player2 && !winnerId) ? (
                                <VStack align="flex-end" spacing={1}>
                                    <Tag colorScheme="blue" size="md">Por Jugar</Tag>
                                    {matchDate && (
                                        <Text fontSize="xs" color="gray.500">
                                            {format(matchDate, "d MMM, HH:mm'h'", { locale: es })}
                                        </Text>
                                    )}
                                </VStack>
                              ) : null}
                            </Box>
                          </HStack>
                          {matchDate && (
                            <Text fontSize="xs" color="gray.500" pt={2} borderTopWidth="1px" borderColor="gray.200" mt={2}>
                          {winnerId
                          ? `Jugado el: ${format(matchDate, "d MMM, yyyy 'a las' HH:mm'h'", { locale: es })}`
                          : `Agendado para: ${format(matchDate, "d MMM, yyyy 'a las' HH:mm'h'", { locale: es })}`}
                          </Text>
                          )}
                        </VStack>
                      );
                    })}
                  </Box>
                )})}
              </Box>
            </VStack>
          </SimpleGrid>
          }
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}